import { pickTruthy } from '@merakiplace/utils';
import * as Sentry from '@sentry/react';
import { useEffect, useEffectEvent } from 'react';
import { toast } from 'react-toastify';

import { type KakaoSdk, loadKakaoSdk } from '@/hooks/useKakaoShare/kakaoSdk';

const KAKAO_OPEN_FAILED_MESSAGE = '카카오톡을 열지 못했어요';
const KAKAO_SDK_LOADING_MESSAGE = '카카오톡을 준비하고 있어요.\n잠시 후 다시 눌러주세요.';
const FAILURE_TOAST_AUTO_CLOSE_MS = 3000;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

const useKakaoShare = (rawQuery: URLSearchParams) => {
  const query = new URLSearchParams([...rawQuery].filter(([, value]) => value !== 'undefined'));

  const env = query.get('env');
  const isDEV = env === 'DEV';
  const key = isDEV ? 'a9e02f926c48b00bdcb8c3e83d11fbfe' : '2c4f0c728f06faabeb28a15c9ca9d369';

  const title = query.get('title');
  const description = query.get('description');
  const imageUrl = query.get('imageUrl');
  const imageHeight = query.get('imageHeight');
  const imageWidth = query.get('imageWidth');
  const mobileWebUrl = query.get('mobileWebUrl');
  const webUrl = query.get('webUrl');
  const androidExecutionParams = query.get('androidExecutionParams');
  const iosExecutionParams = query.get('iosExecutionParams');
  const buttonTitle = query.get('buttonTitle');

  const reward_policy_internal_name = query.get('rewardPolicyInternalName');
  const share_type = query.get('share_type');
  const tx_id = query.get('tx_id');
  const dong_id = query.get('dong_id');

  const userIdMatch = navigator.userAgent?.match(/userId=(\d+)/);
  const userId = userIdMatch ? Number(userIdMatch[1]) : null;

  const linkObject = pickTruthy({
    mobileWebUrl,
    webUrl,
    androidExecutionParams,
    iosExecutionParams,
  });

  const contentObject = {
    ...pickTruthy({ title, description, imageUrl }),
    imageHeight: Number(imageHeight) || 120,
    imageWidth: Number(imageWidth) || 188,
    link: linkObject,
  };

  const buttonObject = {
    title: buttonTitle || '앱에서 보기',
    link: linkObject,
  };

  // 서버 계약: TS 서버 `libs/modules/point-mall-data/api/src/kakao-share.api.ts`
  const serverCallbackArgs = share_type
    ? pickTruthy({ share_type, tx_id, dong_id, user_id: userId })
    : pickTruthy({ reward_policy_internal_name, user_id: userId });

  const notifyKakaoShareFailed = () =>
    toast(KAKAO_OPEN_FAILED_MESSAGE, { autoClose: FAILURE_TOAST_AUTO_CLOSE_MS });

  const send = (kakao: KakaoSdk) => {
    try {
      if (!kakao.isInitialized()) {
        kakao.init(key);
      }

      kakao.Share.sendDefault({
        objectType: 'feed',
        content: contentObject,
        buttons: [buttonObject],
        serverCallbackArgs,
      });
    } catch (error) {
      notifyKakaoShareFailed();
      Sentry.captureException(error, { tags: { feature: 'kakao-share', stage: 'send-default' } });
    }
  };

  const reportSdkLoadFailed = (error: unknown) => {
    notifyKakaoShareFailed();
    Sentry.captureException(error, {
      level: 'warning',
      tags: { feature: 'kakao-share', stage: 'sdk-load' },
    });
  };

  const kakaoShare = () => {
    if (window.Kakao) {
      send(window.Kakao);
      return;
    }
    // 로드가 끝난 뒤 공유하면 누른 손짓과 멀어져 iOS 가 카카오톡 전환을 막을 수 있어, 다시 누르게 한다
    toast(KAKAO_SDK_LOADING_MESSAGE, { autoClose: FAILURE_TOAST_AUTO_CLOSE_MS });
    loadKakaoSdk().catch(reportSdkLoadFailed);
  };

  // iOS Safari 는 손짓 없는 카카오톡 전환을 무시해, iOS 에서는 미리 불러 두기만 하고 버튼을 누를 때 공유한다
  const shareOnLoad = useEffectEvent((kakao: KakaoSdk) => {
    if (!isIOS()) {
      send(kakao);
    }
  });

  useEffect(() => {
    let cancelled = false;
    loadKakaoSdk().then(
      (kakao) => {
        if (!cancelled) {
          shareOnLoad(kakao);
        }
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, []);

  return kakaoShare;
};

export default useKakaoShare;
